import { useState, useEffect } from 'react';
import { 
  MessageSquare, Gift, Video, BookOpen, ClipboardList, 
  Trophy, Target, Sparkles, Users, User, Eye,
  ArrowRight, ArrowLeft, Loader2, Wand2, Plus, Trash2,
  Clock, Calendar, GripVertical
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';
import { toast } from 'sonner';

interface CreateInterventionWizardProps {
  companyId: string;
  onComplete: () => void;
  onCancel: () => void;
}

interface Worker {
  id: string;
  display_name: string;
  team: string;
}

interface Cluster {
  id: string;
  cluster_name: string;
}

interface SurveyQuestion {
  id: string;
  question: string;
  type: 'scale' | 'multiple_choice' | 'text';
  options?: string[];
  required: boolean;
}

interface ChallengeConfig {
  goal: string;
  metric: string;
  targetValue: number;
  durationDays: number;
}

interface LearningContent {
  points: string[];
  quizQuestion?: string;
  quizOptions?: string[];
  quizAnswer?: number;
}

const DELIVERY_METHODS = [
  { id: 'popup', label: 'Popup', icon: <MessageSquare className="h-5 w-5" />, description: 'Auto-show on home page' },
  { id: 'quest', label: 'Quest', icon: <Gift className="h-5 w-5" />, description: 'Gamified task with XP' },
];

// Categories organized by delivery method
const POPUP_CATEGORIES = [
  { id: 'norm_nudge', label: 'Norm Nudge', icon: <Target className="h-4 w-4" />, description: 'Social proof messaging', needsSetup: false },
  { id: 'recognition', label: 'Recognition', icon: <Sparkles className="h-4 w-4" />, description: 'Acknowledge achievement', needsSetup: false },
  { id: 'success_story', label: 'Success Story', icon: <Eye className="h-4 w-4" />, description: 'Share peer success', needsSetup: false },
  { id: 'video_message', label: 'Video Message', icon: <Video className="h-4 w-4" />, description: 'Video content', needsSetup: false },
];

const QUEST_CATEGORIES = [
  { id: 'survey', label: 'Survey', icon: <ClipboardList className="h-4 w-4" />, description: 'Collect feedback', needsSetup: true },
  { id: 'micro_learning', label: 'Micro Learning', icon: <BookOpen className="h-4 w-4" />, description: 'Short educational content', needsSetup: true },
  { id: 'challenge', label: 'Challenge', icon: <Trophy className="h-4 w-4" />, description: 'Behavior change challenge', needsSetup: true },
  { id: 'commitment', label: 'Commitment', icon: <Target className="h-4 w-4" />, description: 'Implementation intentions', needsSetup: false },
  { id: 'peer_observation', label: 'Peer Observation', icon: <Eye className="h-4 w-4" />, description: 'Observe and report safe behavior', needsSetup: true },
  { id: 'record_safety_message', label: 'Record Safety Message', icon: <Video className="h-4 w-4" />, description: 'Record video sharing safety tip', needsSetup: true },
  { id: 'cluster_challenge', label: 'Cluster Challenge', icon: <Users className="h-4 w-4" />, description: 'Team-based behavior challenge', needsSetup: true },
];

// Get categories based on delivery method
const getCategories = (method: string) => method === 'popup' ? POPUP_CATEGORIES : QUEST_CATEGORIES;

const TARGET_TYPES = [
  { id: 'individual', label: 'Individual', icon: <User className="h-4 w-4" /> },
  { id: 'cluster', label: 'Cluster', icon: <Users className="h-4 w-4" /> },
  { id: 'team', label: 'Team', icon: <Users className="h-4 w-4" /> },
  { id: 'all', label: 'All Workers', icon: <Users className="h-4 w-4" /> },
];

export default function CreateInterventionWizard({ companyId, onComplete, onCancel }: CreateInterventionWizardProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);

  // Form state
  const [deliveryMethod, setDeliveryMethod] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [targetType, setTargetType] = useState<string>('');
  const [targetWorkerId, setTargetWorkerId] = useState<string>('');
  const [targetClusterId, setTargetClusterId] = useState<string>('');
  const [targetTeam, setTargetTeam] = useState<string>('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [xpReward, setXpReward] = useState(10);
  const [useAiContent, setUseAiContent] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  // Category-specific state
  const [surveyQuestions, setSurveyQuestions] = useState<SurveyQuestion[]>([
    { id: '1', question: '', type: 'scale', required: true }
  ]);
  const [challengeConfig, setChallengeConfig] = useState<ChallengeConfig>({
    goal: '',
    metric: 'days_without_violation',
    targetValue: 7,
    durationDays: 7
  });
  const [learningContent, setLearningContent] = useState<LearningContent>({
    points: [''],
    quizQuestion: '',
    quizOptions: ['', '', '', ''],
    quizAnswer: 0
  });

  // Delivery timing state
  const [deliveryTiming, setDeliveryTiming] = useState<'now' | 'scheduled'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');

  // Data
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [teams, setTeams] = useState<string[]>([]);

  // Determine if category needs setup step
  const currentCategories = getCategories(deliveryMethod);
  const categoryNeedsSetup = currentCategories.find(c => c.id === category)?.needsSetup || false;
  const totalSteps = categoryNeedsSetup ? 6 : 5;
  const stepLabels = categoryNeedsSetup 
    ? ['Delivery', 'Category', 'Setup', 'Target', 'Content', 'Timing']
    : ['Delivery', 'Category', 'Target', 'Content', 'Timing'];

  useEffect(() => {
    loadTargetOptions();
  }, [companyId]);

  const loadTargetOptions = async () => {
    const { data: workersData } = await supabase
      .from('worker_profiles')
      .select('id, display_name, team')
      .eq('company_id', companyId);
    setWorkers(workersData || []);

    const uniqueTeams = [...new Set((workersData || []).map(w => w.team))].filter(Boolean);
    setTeams(uniqueTeams);

    const { data: clustersData } = await supabase
      .from('worker_clusters')
      .select('id, cluster_name')
      .eq('company_id', companyId);
    setClusters(clustersData || []);
  };

  const handleGenerateContent = async () => {
    if (!aiPrompt) {
      toast.error('Please enter a prompt for AI content generation');
      return;
    }

    setGeneratingContent(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-intervention-content', {
        body: { category, prompt: aiPrompt, targetType, companyId },
      });

      if (error) throw error;
      if (data?.title) setTitle(data.title);
      if (data?.message) setMessage(data.message);
      toast.success('Content generated!');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content');
    } finally {
      setGeneratingContent(false);
    }
  };

  // Survey question management
  const addSurveyQuestion = () => {
    setSurveyQuestions([...surveyQuestions, {
      id: Date.now().toString(),
      question: '',
      type: 'scale',
      required: true
    }]);
  };

  const removeSurveyQuestion = (id: string) => {
    if (surveyQuestions.length > 1) {
      setSurveyQuestions(surveyQuestions.filter(q => q.id !== id));
    }
  };

  const updateSurveyQuestion = (id: string, updates: Partial<SurveyQuestion>) => {
    setSurveyQuestions(surveyQuestions.map(q => 
      q.id === id ? { ...q, ...updates } : q
    ));
  };

  // Learning points management
  const addLearningPoint = () => {
    setLearningContent({ ...learningContent, points: [...learningContent.points, ''] });
  };

  const removeLearningPoint = (index: number) => {
    if (learningContent.points.length > 1) {
      const newPoints = [...learningContent.points];
      newPoints.splice(index, 1);
      setLearningContent({ ...learningContent, points: newPoints });
    }
  };

  const updateLearningPoint = (index: number, value: string) => {
    const newPoints = [...learningContent.points];
    newPoints[index] = value;
    setLearningContent({ ...learningContent, points: newPoints });
  };

  const handleSubmit = async () => {
    if (!title) {
      toast.error('Title is required');
      return;
    }

    setLoading(true);
    try {
      let targetWorkerIds: string[] = [];

      if (targetType === 'individual' && targetWorkerId) {
        targetWorkerIds = [targetWorkerId];
      } else if (targetType === 'cluster' && targetClusterId) {
        const { data: cluster } = await supabase
          .from('worker_clusters')
          .select('member_ids')
          .eq('id', targetClusterId)
          .single();
        targetWorkerIds = cluster?.member_ids || [];
      } else if (targetType === 'team' && targetTeam) {
        const { data: teamWorkers } = await supabase
          .from('worker_profiles')
          .select('id')
          .eq('company_id', companyId)
          .eq('team', targetTeam);
        targetWorkerIds = (teamWorkers || []).map(w => w.id);
      } else if (targetType === 'all') {
        const { data: allWorkers } = await supabase
          .from('worker_profiles')
          .select('id')
          .eq('company_id', companyId);
        targetWorkerIds = (allWorkers || []).map(w => w.id);
      }

      if (targetWorkerIds.length === 0) {
        toast.error('No target workers found');
        setLoading(false);
        return;
      }

      // Build category-specific content
      let content: Json = {};
      if (category === 'survey') {
        content = { questions: surveyQuestions.filter(q => q.question.trim()) } as unknown as Json;
      } else if (category === 'challenge') {
        content = { challenge: challengeConfig } as unknown as Json;
      } else if (category === 'micro_learning') {
        content = { 
          learning: {
            points: learningContent.points.filter(p => p.trim()),
            quiz: learningContent.quizQuestion ? {
              question: learningContent.quizQuestion,
              options: learningContent.quizOptions?.filter(o => o.trim()),
              answer: learningContent.quizAnswer
            } : null
          }
        } as unknown as Json;
      }

      // Calculate scheduled delivery time
      let deliveredAt = null;
      if (deliveryTiming === 'now') {
        deliveredAt = new Date().toISOString();
      }

      const scheduledFor = deliveryTiming === 'scheduled' && scheduledDate 
        ? new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString()
        : null;

      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 48);

      // For quest delivery method, create quest_templates and worker_quests
      if (deliveryMethod === 'quest') {
        // Map category to quest_type
        const questTypeMap: Record<string, string> = {
          survey: 'survey',
          micro_learning: 'learning',
          challenge: 'challenge',
          commitment: 'commitment',
          observation: 'observation',
        };
        const questType = questTypeMap[category] || 'learning';

        // Create quest template
        const { data: templateData, error: templateError } = await supabase
          .from('quest_templates')
          .insert({
            company_id: companyId,
            title,
            description: message || title,
            quest_type: questType,
            intervention_type: category,
            xp_reward: xpReward,
            content,
            duration_hours: 48,
            is_active: true,
          })
          .select('id')
          .single();

        if (templateError) throw templateError;

        // Create worker_quests for each target worker
        const workerQuests = targetWorkerIds.map(workerId => ({
          worker_id: workerId,
          template_id: templateData.id,
          status: 'pending',
          expires_at: expiresAt.toISOString(),
          response_data: {},
        }));

        const { data: questsData, error: questsError } = await supabase
          .from('worker_quests')
          .insert(workerQuests)
          .select('id, worker_id');

        if (questsError) throw questsError;

        // Create interventions linked to worker_quests
        const interventions = (questsData || []).map(quest => ({
          company_id: companyId,
          delivery_method: deliveryMethod,
          category,
          target_type: targetType,
          target_worker_id: quest.worker_id,
          target_cluster_id: targetType === 'cluster' ? targetClusterId : null,
          target_team: targetType === 'team' ? targetTeam : null,
          trigger_type: 'manual',
          title,
          message: message || null,
          video_url: videoUrl || null,
          xp_reward: xpReward,
          status: deliveryTiming === 'now' ? 'pending' : 'scheduled',
          delivered_at: deliveredAt,
          expires_at: expiresAt.toISOString(),
          content,
          worker_quest_id: quest.id,
        }));

        const { error: interventionError } = await supabase.from('interventions').insert(interventions);
        if (interventionError) throw interventionError;
      } else {
        // For popup delivery, just create interventions (no quest)
        const interventions = targetWorkerIds.map(workerId => ({
          company_id: companyId,
          delivery_method: deliveryMethod,
          category,
          target_type: targetType,
          target_worker_id: workerId,
          target_cluster_id: targetType === 'cluster' ? targetClusterId : null,
          target_team: targetType === 'team' ? targetTeam : null,
          trigger_type: 'manual',
          title,
          message: message || null,
          video_url: videoUrl || null,
          xp_reward: xpReward,
          status: deliveryTiming === 'now' ? 'pending' : 'scheduled',
          delivered_at: deliveredAt,
          expires_at: scheduledFor,
          content,
        }));

        const { error } = await supabase.from('interventions').insert(interventions);
        if (error) throw error;
      }

      const timingMessage = deliveryTiming === 'now' 
        ? 'and delivered' 
        : `scheduled for ${scheduledDate} ${scheduledTime}`;
      toast.success(`Created ${targetWorkerIds.length} intervention(s) ${timingMessage}`);
      onComplete();
    } catch (error) {
      console.error('Error creating intervention:', error);
      toast.error('Failed to create intervention');
    } finally {
      setLoading(false);
    }
  };

  const getActualStep = () => {
    if (!categoryNeedsSetup) {
      // No setup step: 1-Delivery, 2-Category, 3-Target, 4-Content, 5-Timing
      return step;
    }
    // With setup step: 1-Delivery, 2-Category, 3-Setup, 4-Target, 5-Content, 6-Timing
    return step;
  };

  const canProceed = () => {
    const actualStep = getActualStep();
    
    if (actualStep === 1) return !!deliveryMethod;
    if (actualStep === 2) return !!category;
    
    if (categoryNeedsSetup) {
      if (actualStep === 3) {
        // Validate category-specific setup
        if (category === 'survey') {
          return surveyQuestions.some(q => q.question.trim());
        }
        if (category === 'challenge') {
          return challengeConfig.goal.trim() !== '';
        }
        if (category === 'micro_learning') {
          return learningContent.points.some(p => p.trim());
        }
        return true;
      }
      if (actualStep === 4) {
        return !!targetType && (
          targetType === 'all' ||
          (targetType === 'individual' && targetWorkerId) ||
          (targetType === 'cluster' && targetClusterId) ||
          (targetType === 'team' && targetTeam)
        );
      }
      if (actualStep === 5) return !!title;
      if (actualStep === 6) {
        return deliveryTiming === 'now' || (deliveryTiming === 'scheduled' && scheduledDate);
      }
    } else {
      if (actualStep === 3) {
        return !!targetType && (
          targetType === 'all' ||
          (targetType === 'individual' && targetWorkerId) ||
          (targetType === 'cluster' && targetClusterId) ||
          (targetType === 'team' && targetTeam)
        );
      }
      if (actualStep === 4) return !!title;
      if (actualStep === 5) {
        return deliveryTiming === 'now' || (deliveryTiming === 'scheduled' && scheduledDate);
      }
    }
    
    return false;
  };

  const renderSetupStep = () => {
    if (category === 'survey') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Survey Questions</h3>
            <Button variant="outline" size="sm" onClick={addSurveyQuestion}>
              <Plus className="h-4 w-4 mr-1" />
              Add Question
            </Button>
          </div>

          <div className="space-y-4">
            {surveyQuestions.map((q, index) => (
              <Card key={q.id} className="p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <GripVertical className="h-5 w-5 text-muted-foreground mt-2" />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">Q{index + 1}</span>
                      <Input
                        placeholder="Enter your question..."
                        value={q.question}
                        onChange={(e) => updateSurveyQuestion(q.id, { question: e.target.value })}
                        className="flex-1"
                      />
                      {surveyQuestions.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeSurveyQuestion(q.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Select 
                        value={q.type} 
                        onValueChange={(v) => updateSurveyQuestion(q.id, { type: v as SurveyQuestion['type'] })}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="scale">1-5 Scale</SelectItem>
                          <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                          <SelectItem value="text">Open Text</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <div className="flex items-center gap-2">
                        <Switch 
                          id={`required-${q.id}`}
                          checked={q.required}
                          onCheckedChange={(v) => updateSurveyQuestion(q.id, { required: v })}
                        />
                        <Label htmlFor={`required-${q.id}`} className="text-sm">Required</Label>
                      </div>
                    </div>

                    {q.type === 'multiple_choice' && (
                      <div className="space-y-2 pl-4">
                        {(q.options || ['', '', '']).map((opt, optIndex) => (
                          <Input
                            key={optIndex}
                            placeholder={`Option ${optIndex + 1}`}
                            value={opt}
                            onChange={(e) => {
                              const newOptions = [...(q.options || ['', '', ''])];
                              newOptions[optIndex] = e.target.value;
                              updateSurveyQuestion(q.id, { options: newOptions });
                            }}
                            className="max-w-xs"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    if (category === 'challenge') {
      return (
        <div className="space-y-4">
          <h3 className="font-semibold">Challenge Configuration</h3>
          
          <div className="space-y-4">
            <div>
              <Label>Challenge Goal *</Label>
              <Textarea
                placeholder="e.g., Complete 7 days without any safety violations"
                value={challengeConfig.goal}
                onChange={(e) => setChallengeConfig({ ...challengeConfig, goal: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <Label>Success Metric</Label>
              <Select 
                value={challengeConfig.metric}
                onValueChange={(v) => setChallengeConfig({ ...challengeConfig, metric: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="days_without_violation">Days without violation</SelectItem>
                  <SelectItem value="hazard_reports">Hazard reports submitted</SelectItem>
                  <SelectItem value="checkins_completed">Check-ins completed</SelectItem>
                  <SelectItem value="quests_completed">Quests completed</SelectItem>
                  <SelectItem value="ppe_compliance">PPE compliance rate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Target Value: {challengeConfig.targetValue}</Label>
              <Slider
                value={[challengeConfig.targetValue]}
                onValueChange={([v]) => setChallengeConfig({ ...challengeConfig, targetValue: v })}
                min={1}
                max={30}
                step={1}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Duration (days): {challengeConfig.durationDays}</Label>
              <Slider
                value={[challengeConfig.durationDays]}
                onValueChange={([v]) => setChallengeConfig({ ...challengeConfig, durationDays: v })}
                min={1}
                max={30}
                step={1}
                className="mt-2"
              />
            </div>
          </div>
        </div>
      );
    }

    if (category === 'micro_learning') {
      return (
        <div className="space-y-4">
          <h3 className="font-semibold">Learning Content</h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Key Learning Points</Label>
                <Button variant="outline" size="sm" onClick={addLearningPoint}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Point
                </Button>
              </div>
              
              <div className="space-y-2">
                {learningContent.points.map((point, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground w-6">{index + 1}.</span>
                    <Input
                      placeholder="Enter learning point..."
                      value={point}
                      onChange={(e) => updateLearningPoint(index, e.target.value)}
                      className="flex-1"
                    />
                    {learningContent.points.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removeLearningPoint(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Card className="p-4 bg-muted/30">
              <Label className="text-sm font-medium">Optional Quiz Question</Label>
              <p className="text-xs text-muted-foreground mb-3">Add a quiz to test comprehension</p>
              
              <div className="space-y-3">
                <Input
                  placeholder="Quiz question..."
                  value={learningContent.quizQuestion}
                  onChange={(e) => setLearningContent({ ...learningContent, quizQuestion: e.target.value })}
                />
                
                {learningContent.quizQuestion && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {learningContent.quizOptions?.map((opt, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="quiz-answer"
                            checked={learningContent.quizAnswer === index}
                            onChange={() => setLearningContent({ ...learningContent, quizAnswer: index })}
                            className="accent-primary"
                          />
                          <Input
                            placeholder={`Option ${index + 1}`}
                            value={opt}
                            onChange={(e) => {
                              const newOptions = [...(learningContent.quizOptions || [])];
                              newOptions[index] = e.target.value;
                              setLearningContent({ ...learningContent, quizOptions: newOptions });
                            }}
                            className="flex-1"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Select the radio button next to the correct answer</p>
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderTimingStep = () => {
    return (
      <div className="space-y-4">
        <h3 className="font-semibold">Delivery Timing</h3>
        
        <RadioGroup value={deliveryTiming} onValueChange={(v) => setDeliveryTiming(v as 'now' | 'scheduled')}>
          <div className="grid grid-cols-2 gap-4">
            <Card 
              className={`p-4 cursor-pointer transition-all ${
                deliveryTiming === 'now' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
              }`}
              onClick={() => setDeliveryTiming('now')}
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value="now" id="now" />
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <div>
                    <Label htmlFor="now" className="font-medium cursor-pointer">Deliver Now</Label>
                    <p className="text-xs text-muted-foreground">Send immediately</p>
                  </div>
                </div>
              </div>
            </Card>
            
            <Card 
              className={`p-4 cursor-pointer transition-all ${
                deliveryTiming === 'scheduled' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
              }`}
              onClick={() => setDeliveryTiming('scheduled')}
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value="scheduled" id="scheduled" />
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  <div>
                    <Label htmlFor="scheduled" className="font-medium cursor-pointer">Schedule</Label>
                    <p className="text-xs text-muted-foreground">Choose date & time</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </RadioGroup>

        {deliveryTiming === 'scheduled' && (
          <Card className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Intervention will be delivered at the scheduled time
            </p>
          </Card>
        )}
      </div>
    );
  };

  const renderCurrentStep = () => {
    const actualStep = getActualStep();

    // Step 1: Delivery Method
    if (actualStep === 1) {
      return (
        <div className="space-y-4">
          <h3 className="font-semibold">Choose Delivery Method</h3>
          <div className="grid grid-cols-2 gap-4">
            {DELIVERY_METHODS.map((method) => (
              <Card
                key={method.id}
                className={`p-4 cursor-pointer transition-all ${
                  deliveryMethod === method.id ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setDeliveryMethod(method.id)}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    {method.icon}
                  </div>
                  <span className="font-medium">{method.label}</span>
                </div>
                <p className="text-sm text-muted-foreground">{method.description}</p>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    // Step 2: Category
    if (actualStep === 2) {
      return (
        <div className="space-y-4">
          <h3 className="font-semibold">Choose Category</h3>
          <p className="text-sm text-muted-foreground mb-2">
            {deliveryMethod === 'popup' ? 'Popup categories (auto-show on home)' : 'Quest categories (gamified tasks)'}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {getCategories(deliveryMethod).map((cat) => (
              <Card
                key={cat.id}
                className={`p-3 cursor-pointer transition-all ${
                  category === cat.id ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setCategory(cat.id)}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                    {cat.icon}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{cat.label}</p>
                    <p className="text-xs text-muted-foreground">{cat.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    // Conditional step 3 based on category
    if (categoryNeedsSetup) {
      if (actualStep === 3) return renderSetupStep();
      if (actualStep === 4) return renderTargetStep();
      if (actualStep === 5) return renderContentStep();
      if (actualStep === 6) return renderTimingStep();
    } else {
      if (actualStep === 3) return renderTargetStep();
      if (actualStep === 4) return renderContentStep();
      if (actualStep === 5) return renderTimingStep();
    }

    return null;
  };

  const renderTargetStep = () => (
    <div className="space-y-4">
      <h3 className="font-semibold">Choose Target Audience</h3>
      
      <RadioGroup value={targetType} onValueChange={setTargetType}>
        {TARGET_TYPES.map((type) => (
          <div key={type.id} className="flex items-center space-x-2">
            <RadioGroupItem value={type.id} id={type.id} />
            <Label htmlFor={type.id} className="flex items-center gap-2 cursor-pointer">
              {type.icon}
              {type.label}
            </Label>
          </div>
        ))}
      </RadioGroup>

      {targetType === 'individual' && (
        <Select value={targetWorkerId} onValueChange={setTargetWorkerId}>
          <SelectTrigger>
            <SelectValue placeholder="Select worker" />
          </SelectTrigger>
          <SelectContent>
            {workers.map((worker) => (
              <SelectItem key={worker.id} value={worker.id}>
                {worker.display_name} ({worker.team})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {targetType === 'cluster' && (
        <Select value={targetClusterId} onValueChange={setTargetClusterId}>
          <SelectTrigger>
            <SelectValue placeholder="Select cluster" />
          </SelectTrigger>
          <SelectContent>
            {clusters.map((cluster) => (
              <SelectItem key={cluster.id} value={cluster.id}>
                {cluster.cluster_name || cluster.id.slice(0, 8)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {targetType === 'team' && (
        <Select value={targetTeam} onValueChange={setTargetTeam}>
          <SelectTrigger>
            <SelectValue placeholder="Select team" />
          </SelectTrigger>
          <SelectContent>
            {teams.map((team) => (
              <SelectItem key={team} value={team}>
                {team}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  const renderContentStep = () => (
    <div className="space-y-4">
      <h3 className="font-semibold">Create Content</h3>

      <Card className="p-4 bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-purple-500" />
            <span className="font-medium">AI Content Generation</span>
          </div>
          <Switch checked={useAiContent} onCheckedChange={setUseAiContent} />
        </div>
        {useAiContent && (
          <div className="space-y-3">
            <Textarea
              placeholder="Describe what content you want AI to generate..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={3}
            />
            <Button variant="outline" onClick={handleGenerateContent} disabled={generatingContent}>
              {generatingContent ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate Content
                </>
              )}
            </Button>
          </div>
        )}
      </Card>

      <div className="space-y-3">
        <div>
          <Label>Title *</Label>
          <Input placeholder="Enter title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div>
          <Label>Message</Label>
          <Textarea
            placeholder="Enter message content"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
          />
        </div>

        {(category === 'video_message' || deliveryMethod === 'popup') && (
          <div>
            <Label>Video URL (YouTube)</Label>
            <Input
              placeholder="https://youtube.com/watch?v=..."
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
          </div>
        )}

        <div>
          <Label>XP Reward</Label>
          <Input
            type="number"
            value={xpReward}
            onChange={(e) => setXpReward(parseInt(e.target.value) || 0)}
            min={0}
            max={100}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center justify-between text-sm overflow-x-auto pb-2">
        {stepLabels.map((label, index) => (
          <div key={label} className="flex items-center flex-shrink-0">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step > index + 1 ? 'bg-primary text-primary-foreground' :
              step === index + 1 ? 'bg-primary text-primary-foreground' :
              'bg-muted text-muted-foreground'
            }`}>
              {index + 1}
            </div>
            <span className={`ml-2 ${step === index + 1 ? 'font-medium' : 'text-muted-foreground'}`}>
              {label}
            </span>
            {index < stepLabels.length - 1 && <div className="w-4 md:w-8 h-px bg-border mx-2" />}
          </div>
        ))}
      </div>

      {/* Current step content */}
      {renderCurrentStep()}

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : onCancel()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {step > 1 ? 'Back' : 'Cancel'}
        </Button>

        {step < totalSteps ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading || !canProceed()}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              deliveryTiming === 'now' ? 'Create & Deliver' : 'Schedule Intervention'
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
